import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  UntypedFormArray,
} from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import {
  MomentDateAdapter,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
} from '@angular/material-moment-adapter';
import moment from 'moment';
import { Observable, Subject, throwError } from 'rxjs';
import { takeUntil, tap, catchError, finalize } from 'rxjs/operators';

//SERVICES
import { ClientPaymentProfileFormComponent } from 'v2/modules/client/components/client-payment-profile-form/client-payment-profile-form.component';

import {
  ICompany,
  IBusinessAccountContact,
  IClientShipping,
  IClientShippingUI,
} from '../../../client/interfaces';
import {
  ClientCompanyApiService,
  IWarehouseGroup,
  SalesPurgatoryApiService,
} from '../../../shared';
import { DialogConfig, SnackBarConfig } from '../../../shared/configs';
import { RfqApiService } from '../../../shared/services/api/rfq/rfq-api.service';

// INTERFACES
import { trackById, trackByValue } from '../../../shared/util';
import { ISalesPurgatorySettings } from '../../../system-setting/interfaces';
import { InvoiceConfig } from '../../config/invoice.config';
import { IInvoice, IInvoiceLineItem } from '../../interfaces/invoice.interface';

// Configs

// COMPONENTS

import { InvoiceAdvanceSettingsFormComponent } from '../invoice-advance-settings-form/invoice-advance-settings-form.component';
import { InvoiceNoteFormComponent } from '../invoice-note-form/invoice-note-form.component';

import {
  OptionInterface,
  IKeyValue,
} from './../../../shared/interfaces/common.interface';

@Component({
  selector: 'lum-invoice-client-form',
  templateUrl: './invoice-client-form.component.html',
  styleUrls: ['./invoice-client-form.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },

    { provide: MAT_DATE_FORMATS, useValue: InvoiceConfig.dateFormat },
  ],
})
export class InvoiceClientFormComponent implements OnDestroy, OnInit, OnChanges {
  @Input()
  form :UntypedFormGroup;

  @Input()
  companies :ICompany[] | null = [];

  @Input()
  currentCompany :ICompany | null;

  @Input()
  currentInvoice :IInvoice | null;

  @Input()
  currentWarehouseGroup :IWarehouseGroup | null = null;

  @Input()
  shippingAddresses :IClientShipping[] | null = [];

  @Input()
  warehouseGroups :IWarehouseGroup[] | null = [];

  @Input()
  loadingCompanies :boolean = false;

  @Input()
  contacts :IBusinessAccountContact[] | null = [];

  @Input()
  countries :IKeyValue[] | null = [];

  @Input()
  customFieldsEnabled :boolean = false;

  @Input()
  invoiceCreatedFrom :string = '';

  @Output()
  selectCompanyEvent = new EventEmitter<{
    currentCompany :ICompany;
  }>();

  @Output()
  selectContactEvent = new EventEmitter<number>();

  @Output()
  searchClientEvent = new EventEmitter<string>();

  @Output()
  clearClientEvent = new EventEmitter<boolean>();

  @Output()
  scrollPaginate = new EventEmitter<boolean>();

  searchInput = new UntypedFormControl();
  editContact :boolean = false;
  editShipping :boolean = false;
  salesPurgatorySettings :any = {}; // TODO: Create Type
  salesPurgatorySettings$ :Observable<ISalesPurgatorySettings>;
  currentCompany$ :Observable<ICompany | null>;

  trackById = trackById;
  trackByValue = trackByValue;

  dateFormatHint :string = moment()
    .localeData()
    .longDateFormat(InvoiceConfig.dateFormat.parse.dateInput);

  private onDestroy$ = new Subject();

  constructor(
    private cd :ChangeDetectorRef,
    private dialog :MatDialog,
    private clientCompanyApiService :ClientCompanyApiService,
    private salesPurgatoryApiService :SalesPurgatoryApiService,
    private rfqApiService :RfqApiService,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.getDataFromService();
    this.getCurrentCompany().subscribe();
    this.getSalesPurgatorySettings().subscribe();
  }

  ngOnChanges({
    contacts,
    shippingAddresses,
    currentWarehouseGroup,
  } :SimpleChanges) :void {
    if (this.currentCompany) {
      if (contacts?.currentValue && !contacts?.previousValue?.length) {
        this.editContact = contacts.currentValue
          ? this.clientContactHasError
          : false;
      }
      if (
        shippingAddresses?.currentValue &&
        !shippingAddresses?.previousValue?.length
      ) {
        this.editShipping = shippingAddresses.currentValue
          ? this.clientShippingHasError
          : false;
      }

      // If has selected a contact and still overrides some field, then process the override data.
      if (this.form.get('client_contact_id')?.value) {
        this.processOverrideContact();
      }

      if (this.form.get('shipping_address_id')?.value) {
        this.processOverrideShippingAddress();
      }
    }

    if (currentWarehouseGroup?.currentValue?.id) {
      this.onSelectWarehouseGroup(currentWarehouseGroup.currentValue.id);
    }
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  handleAddPayMethod(e :Event) {
    e.preventDefault();

    this.dialog
      .open(ClientPaymentProfileFormComponent, {
        data: {
          creditCard: null,
          client: this.currentCompany,
        },
        width: DialogConfig.sizes.medium,
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((response) => {
        if (response?.id) {
          this.snackBar.open('Saved new payment profile', null, {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        }
      });
  }

  onSelectCompany(company :ICompany) :void {
    if (this.currentCompany?.id && company.id === this.currentCompany.id) {
      return;
    }

    this.selectCompanyEvent.emit({ currentCompany: company });
  }

  onSelectContact(event :MatSelectChange) :void {
    this.selectContactEvent.emit(event.value);
  }

  onOpenInvoiceNoteDialog() :void {
    this.dialog
      .open(InvoiceNoteFormComponent, {
        data: {
          public_note: this.form.get('public_note')?.value,
          private_note: this.form.get('private_note')?.value,
        },
        width: DialogConfig.sizes.medium,
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((notes) => {
        if (notes) {
          this.form.patchValue(notes);
        }
      });
  }

  onOpenInvoiceAdvanceSettingsDialog() :void {
    this.dialog
      .open(InvoiceAdvanceSettingsFormComponent, {
        data: {
          frequency: this.form.get('frequency')?.value,
          payment_term_title: this.form.get('payment_term_title')?.value,
          delivery_type: this.form.get('delivery_type')?.value,
          tags: this.form.get('tags')?.value,
          client_purchase_order_type_id: this.form.get(
            'client_purchase_order_type_id',
          )?.value,
          client_purchase_order_type_label: this.form.get(
            'client_purchase_order_type_label',
          )?.value,
          warehouse_group_id: this.form.get('warehouse_group_id')?.value,
          alternate_ids: this.form.get('alternate_ids')?.value,
          customFieldValues: this.form.get('customFieldValues')?.value || '[]',
          customFieldsEnabled: this.customFieldsEnabled,
          warehouseGroups: this.warehouseGroups,
          sales_channel_id: this.form.get('sales_channel_id')?.value || null,
          createdFrom: this.invoiceCreatedFrom,
          defaultPosChannel: this.form.get('defaultPosChannel')?.value || null,
          order_date: this.form.get('order_date')?.value || null,
          due_date: this.form.get('due_date')?.value || null,
          autopay: this.form.get('autopay')?.value || false,
          autopay_after_days: this.form.get('autopay_after_days')?.value || 0,
          autoPushUpdatesToSalesOrder: this.form.get('autoPushUpdatesToSalesOrder')
            ?.value,
        },
        width: DialogConfig.sizes.medium,
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((settings) => {
        if (settings) {
          this.form.patchValue(settings);
          this.onSelectWarehouseGroup(settings.warehouse_group_id);
          this.alternateIdsFormArray.clear();
          settings.alternate_ids?.forEach((alternate_id :any) => {
            this.alternateIdsFormArray.push(new UntypedFormControl(alternate_id));
          });
        }
      });
  }

  onSelectWarehouseGroup(warehouseGroupId :number) :void {
    const lineItems = this.form.get('line_items')?.value;
    const noEmptySku = lineItems.filter((item :{ sku :string }) => item.sku);
    const ids = noEmptySku.map((item :{ item_id :number }) => item.item_id);

    if (noEmptySku?.length === 0) {
      return;
    }

    this.rfqApiService.setWarehouseGroupOnChangeLoading(true);
    this.rfqApiService
      .getLocationStocksByRfqIds({
        rfqIds: ids.join(','),
        warehouseGroupId: warehouseGroupId ? warehouseGroupId.toString() : null,
      })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.rfqApiService.setWarehouseGroupOnChangeLoading(false)),
        catchError((response) => {
          return throwError(() => response.error);
        }),
      )
      .subscribe((response :IInvoiceLineItem[]) => {
        this.processLineItems(response, noEmptySku, lineItems);
      });
  }

  // Helper function to update items
  private processLineItems(
    response :IInvoiceLineItem[],
    noEmptySku :IInvoiceLineItem[],
    lineItems :IInvoiceLineItem[],
  ) :void {
    noEmptySku.forEach((item :IInvoiceLineItem) => {
      const updateData = response.find((data, index) => data.id === item.item_id);
      if (updateData) {
        // Update only location_stock
        item.location_stock = updateData.location_stock;
        // Find the index of this item in the original lineItems array
        const index = lineItems.findIndex(
          (lineItem) =>
            lineItem.item_id === item.item_id && lineItem.order === item.order,
        );
        if (index !== -1) {
          // Update the item in the original lineItems array
          lineItems[index] = item;
        }
      }
    });
    // Update form control with new lineItems
    this.form.controls['line_items'].patchValue(lineItems);
    this.rfqApiService.setWarehouseGroupOnChangeLoading(false);
  }

  onSearchClientChange(client :string) :void {
    this.searchClientEvent.emit(client);
  }

  displayFn(option :ICompany) :string {
    return option && option.business_name ? option.business_name : '';
  }

  onClearClient() :void {
    this.searchInput.reset();
    this.clearClientEvent.emit(true);
  }

  onOverrideContact() :void {
    this.editContact = !this.editContact;

    if (!this.editContact) {
      const contactId = this.clientContactId;
      const contactLists :IBusinessAccountContact[] = this.contacts ?? [];
      const index :number = contactLists.findIndex(
        (contact :IBusinessAccountContact) => contact.id == contactId,
      );

      if (index >= 0) {
        const currentContact = contactLists[index];
        contactLists.splice(index, 1, {
          ...currentContact,
          userAccount: {
            ...currentContact.userAccount,
            email: this.form.get('shipping_email')?.value,
            phoneOne: this.form.get('shipping_phone')?.value,
            fullName: this.form.get('shipping_name')?.value,
          },
        });
      }
    }
    // Set the value of the form control to empty string to trigger the selection
    if (this.shouldOverrideClientContact) {
      this.form.get('client_contact_id')?.setValue('');
    }
  }

  onOverrideShipping() :void {
    this.editShipping = !this.editShipping;

    if (!this.editShipping) {
      const shippingTemplateId = this.form.get('shipping_address_id')?.value;
      const shippingAddresses :IClientShippingUI[] = this.shippingAddresses ?? [];
      const index :number = shippingAddresses.findIndex(
        (address :IClientShippingUI) => address.id == shippingTemplateId,
      );

      if (index >= 0) {
        const currentShippingTemplate = shippingAddresses[index];

        shippingAddresses.splice(index, 1, {
          ...currentShippingTemplate,
          address: this.form.get('ship_address')?.value,
          addressLine2: this.form.get('ship_address_line_2')?.value,
          city: this.form.get('ship_city')?.value,
          state: this.form.get('ship_state')?.value,
          country: this.form.get('ship_country')?.value,
          postalCode: this.form.get('ship_zip')?.value,
          accountNumber: this.form.get('shipping_account_number')?.value,
          notes: this.form.get('shipping_notes')?.value,
          shippingType: this.form.get('shipping_type')?.value,
        });

        this.cd.detectChanges();
      }
    }
    // Set the value of the form control to empty string to trigger the selection
    if (this.shouldOverrideShippingAddress) {
      this.form.get('shipping_address_id')?.setValue('');
    }
  }

  onSelectShippingAddresss(value: IClientShipping): void {
    const contactLists: IBusinessAccountContact[] = this.contacts ?? [];
    const index: number = contactLists.findIndex(
      (contact: IBusinessAccountContact) => contact.id === this.clientContactId,
    );
    const currentContact = contactLists[index];

    // Patch only when first name and last name is available.
    if (!!(value?.firstName && value?.lastName)) {
      this.form.patchValue({
        shipping_name: value.firstName +' '+ value.lastName,
      });
    } else {
      this.form.patchValue({
        shipping_name: currentContact?.userAccount?.fullName
      });
    }
    this.cd.detectChanges();
  }

  private getDataFromService() :void {
    this.currentCompany$ = this.clientCompanyApiService.currentCompany$;
    this.salesPurgatorySettings$ =
      this.salesPurgatoryApiService.salesPurgatorySettings$;
  }

  private getCurrentCompany() :Observable<ICompany | null> {
    return this.currentCompany$.pipe(
      tap((client :ICompany | null) => {
        if (client) {
          this.form.patchValue({
            client_id: client.id,
            client_name: client.business_name,
          });
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getCompleteCountryName(countryCode :string) :string {
    const country = this.countries?.find((country) => country.key === countryCode);

    return (country?.value as string) || '';
  }

  private onSetAddressText(shipping :IClientShippingUI) :string {
    return `${shipping.address}, ${
      shipping.addressLine2 ? shipping.addressLine2 : ''
    }, ${shipping.city}, ${shipping.state}, ${
      shipping.postalCode
    }, ${this.getCompleteCountryName(shipping.country)}`;
  }

  get overrideShippingAddress() :string {
    return `[${this.form.get('shipping_company')?.value}] ${
      this.form.get('ship_address')?.value
    }, ${this.form.get('ship_address_line_2')?.value}, ${
      this.form.get('ship_city')?.value
    }, ${this.form.get('ship_state')?.value}, ${
      this.form.get('ship_zip')?.value
    }, ${this.getCompleteCountryName(this.form.get('ship_country')?.value)}`;
  }

  private getSalesPurgatorySettings() :Observable<ISalesPurgatorySettings> {
    return this.salesPurgatorySettings$.pipe(
      tap((settings :ISalesPurgatorySettings) => {
        const { value } = settings;
        if (value) {
          this.salesPurgatorySettings = JSON.parse(value);
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private processOverrideContact() :void {
    const contactId = this.clientContactId;
    const contactLists :IBusinessAccountContact[] = this.contacts ?? [];
    const index :number = contactLists.findIndex(
      (contact :IBusinessAccountContact) => contact.id == contactId,
    );

    if (index >= 0) {
      const { fullName, phoneOne, email } = contactLists[index]?.userAccount || {};
      const userAccount = {
        fullName: this.form.get('shipping_name')?.value,
        phoneOne: this.form.get('shipping_phone')?.value,
        email: this.form.get('shipping_email')?.value,
      };

      // Check if any of the fields differ
      if (
        fullName !== userAccount.fullName ||
        phoneOne !== userAccount.phoneOne ||
        email !== userAccount.email
      ) {
        this.form.get('client_contact_id')?.setValue('');
      }
    }
  }

  private processOverrideShippingAddress() :void {
    const shippingTemplateId = this.form.get('shipping_address_id')?.value;
    const shippingAddresses :IClientShippingUI[] = this.shippingAddresses ?? [];
    const index :number = shippingAddresses.findIndex(
      (address :IClientShippingUI) => address.id == shippingTemplateId,
    );

    if (index >= 0) {
      const currentShippingTemplate = shippingAddresses[index];
      currentShippingTemplate.companyName =
        this.form.get('shipping_company')?.value;
    }
  }

  get companyOptions() :OptionInterface[] {
    if (this.companies?.length) {
      return this.companies.map((company) => ({
        value: company.id,
        label: company.business_name,
      }));
    }

    return [];
  }

  get selectedShippingAddress() :string {
    if (this.shippingAddresses?.length) {
      const shippingAddressId = this.form.get('shipping_address_id')?.value;
      const matchedAddress = this.shippingAddresses.find(
        (address) => address.id === shippingAddressId,
      );

      if (matchedAddress) {
        return `[${matchedAddress.companyName}] ${matchedAddress.address}`;
      }
    }

    return '';
  }

  get formattedShippingAddresses() :IClientShippingUI[] {
    return (
      this.shippingAddresses?.map((address :IClientShippingUI) => ({
        ...address,
        addressText: this.onSetAddressText(address),
      })) || []
    );
  }

  get alternateIdsFormArray() :UntypedFormArray {
    return this.form.get('alternate_ids') as UntypedFormArray;
  }

  get pushClientInvoiceToShipstation() :boolean {
    return this.salesPurgatorySettings?.push_client_invoice_to_shipstation || false;
  }

  get clientContactId() :number {
    return this.form.get('client_contact_id')?.value;
  }

  get shippingAddressId() :number {
    return this.form.get('shipping_address_id')?.value;
  }

  get clientContactOverrideValue() :string {
    const company = this.form.get('shipping_company')?.value || '';
    const email = this.form.get('shipping_email')?.value || '';
    const phone = this.form.get('shipping_phone')?.value || '';

    // Construct the contact string based on the availability of email and phone
    const contact = [email, phone].filter(Boolean).join(' / ');

    return `${company}${contact ? ` [${contact}]` : ''}`;
  }

  get shippingAddressOverrideValue() :string {
    return `${this.form.get('shiping_company')?.value} [${
      this.form.get('shipping_email')?.value
    }]`;
  }

  get clientContactHasError() :boolean {
    return (
      this.form.get('shipping_email')?.invalid ||
      this.form.get('shipping_phone')?.invalid ||
      this.form.get('shipping_name')?.invalid ||
      false
    );
  }

  get clientShippingHasError() :boolean {
    return (
      this.form.get('ship_address')?.invalid ||
      this.form.get('ship_city')?.invalid ||
      this.form.get('ship_state')?.invalid ||
      this.form.get('ship_country')?.invalid ||
      this.form.get('ship_zip')?.invalid ||
      this.form.get('shipping_company')?.invalid ||
      false
    );
  }

  get shippingNameValue() :string {
    return this.form.get('shipping_name')?.value?.trim();
  }

  get shippingAddress() :string {
    return this.form.get('ship_address')?.value?.trim();
  }

  get clientControl() :UntypedFormControl {
    return this.form.get('client') as UntypedFormControl;
  }

  get shouldOverrideClientContact() :boolean {
    return !this.clientContactId && !!this.shippingNameValue;
  }

  get shouldOverrideShippingAddress() :boolean {
    return !this.shippingAddressId && !!this.shippingAddress;
  }
}
